/**
 * Feasibility Service - Check idea feasibility before report generation
 * 
 * Now served by Lambda for simpler debugging and better separation
 */

import { lambdaClient } from '../config/http.client';

// Re-export client for backward compatibility
const client = lambdaClient;

/**
 * Check if an idea passes feasibility validation
 * 
 * @param {string} idea - The idea text to validate
 * @param {boolean} questionnaireMode - Whether this is from 8Q structured input
 * @param {string} modelId - Optional model ID to use for validation
 * @returns {Promise<Object>} Validation result
 */
export const checkFeasibility = async (idea, questionnaireMode = false, modelId = null) => {
  try {
    const response = await client.post('/feasibility', {
      idea: idea.trim(),
      questionnaire_mode: questionnaireMode,
      model_id: modelId
    });

    return response.data;
  } catch (error) {
    console.error('Feasibility check failed:', error);
    
    // If the request failed with 200 but returned invalid_idea status
    if (error.response?.data?.status === 'invalid_idea') {
      return error.response.data;
    }
    
    // If service returned error status (500), throw the error
    if (error.response?.status === 500 || error.response?.data?.status === 'error') {
      throw new Error(error.response?.data?.error || error.message || 'Feasibility check service error');
    }
    
    // For network errors or other failures, throw error instead of faking success
    throw error;
  }
};

/**
 * Parse feasibility check result for UI display
 */
export const parseFeasibilityResult = (result) => {
  if (!result) {
    return null;
  }

  // Backend returns { success: true, is_valid: true/false, ... }
  const isValid = result.is_valid === true;

  // Return consistent structure for both valid and invalid cases
  return {
    valid: isValid,
    score: result.feasibility_score || 0,
    issues: result.issues || [],
    suggestions: result.suggestions || [],
    strengths: result.strengths || [],
    opportunities: result.opportunities || [],
    recommendation: result.recommendation || (isValid ? 'proceed' : 'revise'),
    validation_details: result.feasibility_check || {},
    message: result.message || (isValid ? 'Idea passed feasibility validation.' : 'Your idea did not pass feasibility validation.')
  };
};

const feasibilityService = {
  checkFeasibility,
  parseFeasibilityResult
};

export default feasibilityService;
